import { DtoResolution } from '@/shared/constants';
import {
  isVideoFile,
  isAudioFile,
  isImageFile,
  getFileTypeFromBytes,
} from './file-type-check';
import {
  FILE_METADATA_ERROR_MESSAGES,
  formatVideoMetadataLoadError,
} from './file-error-messages';

// Re-export file type check functions
export { isVideoFile, isAudioFile, isImageFile };
export {
  FILE_METADATA_ERROR_MESSAGES,
  formatVideoMetadataLoadError,
} from './file-error-messages';

export interface ParsedFileName {
  fileName: string;
  fileExtension: string;
}

/**
 * 파일 전체 이름 문자열에서 파일 이름과 확장자를 분리합니다.
 *
 * @param fullFileName - 파일 전체 이름 (예: "document.pdf")
 * @returns 파일 이름과 확장자를 포함하는 객체. 확장자가 없거나 '.'으로 시작하거나 끝나는 경우 fileExtension은 빈 문자열입니다.
 *          (예: { fileName: "document", fileExtension: "pdf" })
 */
export const parseFileName = (fullFileName: string): ParsedFileName => {
  const lastDotIndex = fullFileName.lastIndexOf('.');

  // '.'이 없거나, 맨 앞에 있거나, 맨 뒤에 있는 경우 확장자가 없는 것으로 간주
  if (
    lastDotIndex === -1 ||
    lastDotIndex === 0 ||
    lastDotIndex === fullFileName.length - 1
  ) {
    return { fileName: fullFileName, fileExtension: '' };
  }

  const fileName = fullFileName.substring(0, lastDotIndex);
  const fileExtension = fullFileName.substring(lastDotIndex + 1);

  return { fileName, fileExtension };
};

/* ------------------------------------------------------------------------------- */

/**
 * 비디오 해상도를 정규화합니다.
 * 가로/세로 상관없이 더 큰 값을 width로, 작은 값을 height로 반환합니다.
 * 예: 1080x1920 -> 1920x1080
 *
 * @param width - 원본 너비
 * @param height - 원본 높이
 * @returns 정규화된 해상도 {width, height}
 */
export const normalizeResolution = (
  width: number,
  height: number,
): ResolutionDimensions => {
  return width >= height ? { width, height } : { width: height, height: width };
};

export interface ResolutionDimensions {
  width: number;
  height: number;
}

export const resolutionRanges: Record<
  Exclude<DtoResolution, 0>,
  ResolutionDimensions
> = {
  1: { width: 640, height: 480 }, // SD
  2: { width: 1280, height: 720 }, // HD
  3: { width: 1920, height: 1080 }, // FHD
  4: { width: 2560, height: 1440 }, // QHD
  5: { width: 3840, height: 2160 }, // UHD
} as const;

/**
 * 주어진 비디오의 해상도를 가져옵니다.
 * 가로/세로 방향에 상관없이 항상 더 큰 값을 기준으로 해상도를 결정합니다.
 *
 * @param width - 비디오의 너비
 * @param height - 비디오의 높이
 * @returns 해상도 (예: '1(SD)', '2(HD)', '3(FHD)', '4(QHD)', '5(UHD)')
 */
export const getResolutionFromDimensions = (
  width: number,
  height: number,
): DtoResolution => {
  if (width === 0 || height === 0) return 0;

  const normalized = normalizeResolution(width, height);

  const resolutions = Object.entries(resolutionRanges).reverse();

  for (const [resolution, dimensions] of resolutions) {
    if (
      normalized.width >= dimensions.width ||
      normalized.height >= dimensions.height
    ) {
      return Number(resolution) as DtoResolution;
    }
  }

  return 1;
};

export interface VideoMetadataType {
  width: number;
  height: number;
  duration: number;
  fileName: string;
  fileExtension: string;
  fileSize: number;
}

const isValidDuration = (duration: number): boolean =>
  Number.isFinite(duration) && duration > 0;

const waitForValidMediaDuration = (
  media: HTMLMediaElement,
  timeoutMs: number = 3000,
): Promise<number | null> => {
  const readDurationMs = () => {
    const durationMs = Math.floor(media.duration * 1000);
    return isValidDuration(durationMs) ? durationMs : null;
  };

  const immediate = readDurationMs();
  if (immediate !== null) {
    return Promise.resolve(immediate);
  }

  return new Promise((resolve) => {
    let settled = false;

    const onDurationCandidate = () => {
      const durationMs = readDurationMs();
      if (durationMs !== null) {
        finish(durationMs);
      }
    };

    const finish = (value: number | null) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      media.removeEventListener('durationchange', onDurationCandidate);
      media.removeEventListener('loadeddata', onDurationCandidate);
      media.removeEventListener('canplay', onDurationCandidate);
      resolve(value);
    };

    const timeoutId = setTimeout(() => finish(null), timeoutMs);

    media.addEventListener('durationchange', onDurationCandidate);
    media.addEventListener('loadeddata', onDurationCandidate);
    media.addEventListener('canplay', onDurationCandidate);
  });
};

const getDurationFallback = async (
  file: File,
  _trackType: 'video' | 'audio',
): Promise<number | null> => {
  // 방법 1: FFmpeg.wasm이 이미 로드되어 있으면 ffprobe 사용
  try {
    const { isFFmpegLoaded, loadFFmpeg, fetchFile } = await import('./ffmpeg');

    if (isFFmpegLoaded()) {
      const ffmpeg = await loadFFmpeg();
      const inputName = 'duration_check_input';

      await ffmpeg.writeFile(inputName, await fetchFile(file));
      await ffmpeg.ffprobe([
        '-v',
        'error',
        '-show_entries',
        'format=duration',
        '-of',
        'default=noprint_wrappers=1:nokey=1',
        inputName,
        '-o',
        'duration_result.txt',
      ]);

      const durationStr = (await ffmpeg.readFile(
        'duration_result.txt',
        'utf8',
      )) as string;
      await ffmpeg.deleteFile('duration_result.txt');
      await ffmpeg.deleteFile(inputName);

      const duration = parseFloat(durationStr.trim());
      if (isValidDuration(duration * 1000)) {
        return Math.floor(duration * 1000);
      }
    }
  } catch {
    // FFmpeg 방식 실패 시 아래 fallback으로
  }

  // 방법 2: HTML5 currentTime 트릭
  return new Promise((resolve) => {
    const media = document.createElement('video');
    media.preload = 'metadata';
    media.muted = true;
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      media.removeAttribute('src');
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 5000);

    media.onloadedmetadata = () => {
      // 끝으로 시크하여 정확한 duration 유도
      media.currentTime = Number.MAX_SAFE_INTEGER;
    };

    media.ontimeupdate = () => {
      const durationMs = Math.floor(media.duration * 1000);
      if (isValidDuration(durationMs)) {
        clearTimeout(timeoutId);
        cleanup();
        resolve(durationMs);
      }
    };

    media.onerror = () => {
      clearTimeout(timeoutId);
      cleanup();
      resolve(null);
    };

    media.src = objectUrl;
  });
};

/**
 * 비디오 파일의 메타데이터를 가져옵니다.
 *
 * @param file - 비디오 파일
 * @returns 비디오 파일의 메타데이터
 */
export const getVideoMetadata = (file: File): Promise<VideoMetadataType> => {
  return new Promise(async (resolve, reject) => {
    if (!(await isVideoFile(file))) {
      return reject(new Error(FILE_METADATA_ERROR_MESSAGES.NOT_VIDEO));
    }

    const video = document.createElement('video');
    video.preload = 'metadata'; // 메타데이터만 로드하도록 설정
    video.muted = true;
    video.playsInline = true;
    const fileData = parseFileName(file.name);
    let hasRetried = false;

    // video 엘리먼트를 완전히 해제하는 헬퍼
    const destroyVideo = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.srcObject = null;
      // video.load() 호출 제거 - 빈 src로 load() 호출 시 NotSupportedError 발생
    };

    const loadMetadata = () => {
      const objectUrl = URL.createObjectURL(file); // 임시 URL 생성

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl); // URL 메모리 해제 (중요!)
        destroyVideo();
      };

      // 메타데이터 로드 완료 시
      video.onloadedmetadata = async () => {
        let durationMs = await waitForValidMediaDuration(video);
        // cleanup 전에 width/height 캡처 (cleanup 후 video element가 리셋되어 0이 될 수 있음)
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const detectedFileType = await getFileTypeFromBytes(file);
        const resolvedExtension = (
          detectedFileType?.ext || fileData.fileExtension
        ).toLowerCase();

        if (durationMs === null) {
          durationMs = await getDurationFallback(file, 'video');
        }

        if (durationMs === null) {
          cleanup();
          reject(
            new Error(FILE_METADATA_ERROR_MESSAGES.VIDEO_DURATION_UNRESOLVED),
          );
          return;
        }

        cleanup();
        resolve({
          width: videoWidth,
          height: videoHeight,
          duration: durationMs,
          fileName: fileData.fileName,
          fileExtension: resolvedExtension,
          fileSize: file.size,
        });
      };

      // 에러 발생 시
      video.onerror = () => {
        const errorCode = video.error?.code;
        const errorMessage = video.error?.message;
        cleanup();
        if (!hasRetried) {
          hasRetried = true;
          loadMetadata();
          return;
        }
        reject(
          new Error(formatVideoMetadataLoadError(errorCode, errorMessage)),
        );
      };

      video.src = objectUrl; // 비디오 소스 설정하여 로드 시작
      video.load();
    };

    loadMetadata();
  });
};

export interface AudioMetadataType {
  duration: number;
  fileName: string;
  fileExtension: string;
  fileSize: number;
}

/**
 * 오디오 파일의 메타데이터를 가져옵니다.
 *
 * @param file - 오디오 파일
 * @returns 오디오 파일의 메타데이터
 */
export const getAudioMetadata = (file: File): Promise<AudioMetadataType> => {
  return new Promise(async (resolve, reject) => {
    if (!(await isAudioFile(file))) {
      return reject(new Error(FILE_METADATA_ERROR_MESSAGES.NOT_AUDIO));
    }

    const audio = document.createElement('audio');
    audio.preload = 'metadata'; // 메타데이터만 로드하도록 설정

    const objectUrl = URL.createObjectURL(file); // 임시 URL 생성

    const fileData = parseFileName(file.name);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl); // URL 메모리 해제 (중요!)
      audio.removeAttribute('src');
      audio.load();
    };

    // 메타데이터 로드 완료 시
    audio.onloadedmetadata = async () => {
      let durationMs = await waitForValidMediaDuration(audio);
      if (durationMs === null) {
        durationMs = await getDurationFallback(file, 'audio');
      }

      if (durationMs === null) {
        cleanup();
        reject(
          new Error(FILE_METADATA_ERROR_MESSAGES.AUDIO_DURATION_UNRESOLVED),
        );
        return;
      }

      cleanup();
      const detectedFileType = await getFileTypeFromBytes(file);
      const resolvedExtension = (
        detectedFileType?.ext || fileData.fileExtension
      ).toLowerCase();
      resolve({
        duration: durationMs,
        fileName: fileData.fileName,
        fileExtension: resolvedExtension,
        fileSize: file.size,
      });
    };

    // 에러 발생 시
    audio.onerror = () => {
      cleanup();
      reject(new Error(FILE_METADATA_ERROR_MESSAGES.AUDIO_LOAD_FAILED));
    };

    audio.src = objectUrl; // 오디오 소스 설정하여 로드 시작
  });
};

export interface ImageMetadataType {
  width: number;
  height: number;
  fileName: string;
  fileExtension: string;
  fileSize: number;
}

/**
 * 이미지 파일의 메타데이터를 가져옵니다.
 *
 * @param file - 이미지 파일
 * @returns 이미지 파일의 메타데이터
 */
export const getImageMetadata = (file: File): Promise<ImageMetadataType> => {
  return new Promise(async (resolve, reject) => {
    if (!(await isImageFile(file))) {
      return reject(new Error('File is not an image file.'));
    }

    const img = document.createElement('img');
    const objectUrl = URL.createObjectURL(file); // 임시 URL 생성

    const fileData = parseFileName(file.name);

    // 이미지 로드 완료 시
    img.onload = async () => {
      URL.revokeObjectURL(objectUrl); // URL 메모리 해제 (중요!)
      const detectedFileType = await getFileTypeFromBytes(file);
      const resolvedExtension = (
        detectedFileType?.ext || fileData.fileExtension
      ).toLowerCase();
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        fileName: fileData.fileName,
        fileExtension: resolvedExtension,
        fileSize: file.size,
      });
    };

    // 에러 발생 시
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl); // URL 메모리 해제 (중요!)
      reject(new Error('Could not load image metadata.'));
    };

    img.src = objectUrl; // 이미지 소스 설정하여 로드 시작
  });
};

export interface FontMetadataType {
  fileName: string;
  fileExtension: string;
  fileSize: number;
}

/**
 * 폰트 파일의 메타데이터를 가져옵니다.
 *
 * @param file - 폰트 파일
 * @returns 폰트 파일의 메타데이터
 */
export const getFontMetadata = (file: File): Promise<FontMetadataType> => {
  return new Promise(async (resolve, reject) => {
    // 폰트 파일 타입 확인 (ttf, otf, woff, woff2 등)
    const validFontTypes = [
      'font/ttf',
      'font/otf',
      'font/woff',
      'font/woff2',
      'application/x-font-ttf',
      'application/x-font-otf',
      'application/font-woff',
      'application/font-woff2',
    ];

    // file-type/core로 체크 (폰트 파일은 직접 지원하지 않을 수 있으므로 파일명 확장자도 확인)
    const fileTypeResult = await getFileTypeFromBytes(file);
    const isValidFontType =
      fileTypeResult?.mime &&
      validFontTypes.some((type) => fileTypeResult.mime.includes(type));

    if (
      !isValidFontType &&
      !validFontTypes.includes(file.type) &&
      !file.name.match(/\.(ttf|otf|woff|woff2)$/i)
    ) {
      return reject(new Error('File is not a supported font file.'));
    }

    const fileData = parseFileName(file.name);

    // 폰트 파일은 별도의 메타데이터 추출이 필요 없으므로 바로 resolve
    resolve({
      fileName: fileData.fileName,
      fileExtension: fileData.fileExtension,
      fileSize: file.size,
    });
  });
};
